import Text from '@/components/text';
import { Box, Stack, Theme } from '@mui/material';
import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import AddCard from './add-card';
import { CARD_SIZES, CardSize, GridCardCategory, GridCard as GridCardProps } from './helpers';
import GridCard from './grid-card';
import EmptyCard from './empty-card';
import { SortButton } from '../button-bar/helpers';
import ButtonBar from '../button-bar';
import Icon from '../icon';
import Loader from '../loader';

const useStyles = createUseStyles((theme: Theme) => ({
  cardGridHeader: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

interface Props {
  title?: string;
  onAdd?: () => void;
  cards: GridCardProps[];
  categories?: GridCardCategory[];
  canAdd?: boolean;
  showAdd?: boolean;
  addTitle?: string;
  addDisabledTooltip?: string;
  showEmpty?: boolean;
  onSort?: (sortType: string) => void;
  onRefresh?: () => void;
  sortButtons?: SortButton[];
  sortType?: string;
  loading?: boolean;
  size?: CardSize;
}

const CardGrid: FC<Props> = ({
  title,
  onAdd,
  canAdd = true,
  showAdd = true,
  cards,
  addTitle = '',
  addDisabledTooltip,
  categories = [],
  showEmpty = false,
  onSort,
  onRefresh,
  sortButtons = [],
  sortType: activeSort,
  loading = false,
  size = 'small',
}) => {
  const styles = useStyles();
  const { height: cardHeight } = CARD_SIZES[size];

  const hasTitle = !!title;
  const hasCategories = !!categories.length;
  const showEmptyCard = !loading && !hasCategories && showEmpty && !cards.length;
  const hasSort = !!sortButtons.length;

  const handleAdd = () => {
    onAdd?.();
  };

  const handleRefresh = () => {
    onRefresh?.();
  };

  const handleSort = (sortType: string) => {
    onSort?.(sortType);
  };

  const hasRefresh = !!onRefresh;

  return (
    <Box width="100%" pb={2}>
      {hasTitle && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
          className={styles.cardGridHeader}
          width="100%"
          pb={1}
          mb={3}
        >
          <Box>
            <Text size="large">{title}</Text>
          </Box>
          <Stack flexGrow={1} direction="row" justifyContent="flex-end" alignItems="center" gap={1}>
            {hasRefresh && <Icon name="refresh" onClick={handleRefresh} spinOnClick />}
            {hasSort && (
              <ButtonBar onSort={handleSort} sortType={activeSort} buttons={sortButtons} />
            )}
          </Stack>
        </Stack>
      )}
      <Stack gap={2} flexWrap="wrap" flexDirection="row" width="100%">
        {hasCategories &&
          categories.map(({ title, id: categoryId }) => {
            const categoryCards = cards.filter(
              ({ categoryId: cardCatId }) => cardCatId === categoryId
            );

            const showEmptyCat = showEmpty && !categoryCards.length;

            return (
              <Box key={categoryId} width="100%" mb={2}>
                <Box mb={2}>
                  <Text size="large" color="grey">
                    {title}
                  </Text>
                </Box>
                <Stack gap={2} flexWrap="wrap" flexDirection="row">
                  {categoryCards.map((gridCard) => {
                    const { id } = gridCard;
                    return <GridCard gridCard={gridCard} key={id} size={size} />;
                  })}
                </Stack>
                {showEmptyCat && <EmptyCard size={size} />}
              </Box>
            );
          })}
        {!hasCategories &&
          cards.map((gridCard) => {
            const { id } = gridCard;
            return <GridCard gridCard={gridCard} key={id} size={size} />;
          })}

        {showEmptyCard && <EmptyCard size={size} />}

        {loading && (
          <Stack justifyContent="flex-start" width="100%" height={cardHeight} mt={2}>
            <Loader />
          </Stack>
        )}

        {showAdd && (
          <AddCard
            title={addTitle}
            disabled={!canAdd}
            onClick={handleAdd}
            disabledTooltip={addDisabledTooltip}
            size={size}
          />
        )}
      </Stack>
    </Box>
  );
};

export default CardGrid;
